/**
 * A file card (ADR-22 decision 4).
 *
 * The same `[label](artifact:<id>)` link the prose uses inline, rendered as a card because of
 * *where it sits*: an artefact link that is the sole content of a paragraph or of a list item is
 * not a mention inside a sentence, it is the file being offered. The model's closing bullet list
 * of deliverables therefore becomes a list of cards with no new syntax (ADR-23).
 *
 * Everything past the title comes from the transcript's own lineage index — description, format,
 * revision, size — so a card costs no request the chip did not already make.
 */

import { ReactNode } from 'react';
import { ActionIcon, Badge, Box, Group, Skeleton, Stack, Text, Tooltip } from '@mantine/core';
import { IconDownload, IconEye } from '@tabler/icons-react';
import { Artifact } from '~api';
import { useArtifactLabel, useArtifactMeta, useTrackArtifactDownload } from '~portal/contexts';
import { artifactDownloadName, downloadFile } from '~portal/lib';
import { useArtifactSignedUrl } from '~portal/stores/artifact-store';
import { formatFileSize, getFormatIcon } from './artifact-format';
import { artifactCard } from './ArtifactCard.css';
import { stripFormatSuffix, Unavailable } from './ArtifactRef';
import { useArtifactViewer } from './ArtifactViewer';

/**
 * @desc Whether a card shows what the file *is* on top of what it is called: the revision badge,
 * the file name (as opposed to the model's own wording), the byte size and the format label.
 *
 * Off, by operator decision: on a card the size and revision are noise beside the description,
 * the file name is the download's business rather than the reader's, and the format is already
 * said by the icon — so a card is a title and a description. One constant rather than a prop
 * threaded from four call sites, so it is one edit to bring them back — the rendering and its
 * tests stay live behind it.
 */
export const CARD_SHOW_META = false;

export function ArtifactCard({
  artifactId,
  label,
  showMeta = CARD_SHOW_META,
}: {
  artifactId: string;
  label: ReactNode;
  showMeta?: boolean;
}) {
  const { url, format, name, isLoading } = useArtifactSignedUrl(artifactId);
  const meta = useArtifactMeta(artifactId);
  const lineageLabel = useArtifactLabel(artifactId);
  const { open, standalone } = useArtifactViewer();
  const trackDownload = useTrackArtifactDownload();

  if (isLoading) return <Skeleton height={64} radius="md" className={artifactCard.placeholder} />;
  // Same failure the chip shows, for the same reason: a card whose actions are all disabled
  // says nothing about *why*, and a reference that will not resolve has to say so.
  if (!url) return <Unavailable label={label} title="File unavailable" />;

  const extension = meta?.format ?? format ?? 'file';
  const fileName = lineageLabel ?? artifactDownloadName(artifactId, extension, name ?? undefined);
  const text = stripFormatSuffix(typeof label === 'string' ? label : '');
  // Without the meta, the fallback is the deliverable's bare name rather than its file name: a
  // card that says `growth-chart` where the model gave no wording still names the file, and a
  // card with no title at all would be worse than an extension shown.
  const title = text.length > 0 ? text : showMeta ? fileName : (meta?.name ?? fileName);
  const revision = meta?.revision ?? 1;
  const size = formatFileSize(meta?.fileSize);
  const isImage = Artifact.isImageFormat(extension);

  const download = () => {
    trackDownload(extension, 'card');
    downloadFile(url, fileName);
  };
  /** The card's own action, and the chip's and the table row's: preview an image, download the
   * rest. The explicit buttons stay — they say what will happen — but the whole card is the
   * target, because a card that looks like a file should behave like one wherever it is hit. */
  const activate = () => (isImage ? open(artifactId) : download());

  return (
    <Box
      className={artifactCard.card}
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate();
        }
      }}
    >
      <Box component="span" className={artifactCard.icon}>
        {getFormatIcon(extension, 22)}
      </Box>

      <Stack gap={2} className={artifactCard.body}>
        {/* Title row: what the file is called, and — dimmed, after it — what it is. Two rows in
            all, so a list of cards stays a list of files rather than a column of paragraphs. */}
        <Group gap={6} wrap="nowrap" className={artifactCard.titleRow}>
          <Text
            size="sm"
            fw={600}
            className={artifactCard.title}
            title={showMeta ? fileName : undefined}
          >
            {title}
          </Text>
          {showMeta && revision > 1 && (
            <Badge size="xs" variant="light" color="gray">
              v{revision}
            </Badge>
          )}
          {showMeta && (
            <Text size="xs" c="dimmed" tt="uppercase" className={artifactCard.meta}>
              {extension}
            </Text>
          )}
          {showMeta && size && (
            <Text size="xs" c="dimmed" className={artifactCard.meta}>
              · {size}
            </Text>
          )}
        </Group>
        {meta?.description && (
          <Text size="xs" c="dimmed" lineClamp={2}>
            {meta.description}
          </Text>
        )}
      </Stack>

      <Group gap={4} wrap="nowrap" className={artifactCard.actions}>
        {isImage && (
          <Tooltip label="Preview" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={`Preview ${fileName}`}
              onClick={(event) => {
                // The card behind it does the same thing; letting the click through would run
                // the action twice and, for a download, offer the file twice.
                event.stopPropagation();
                open(artifactId);
              }}
            >
              <IconEye size={18} />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label="Download" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={`Download ${fileName}`}
            data-dd-action-name="Download file"
            onClick={(event) => {
              event.stopPropagation();
              download();
            }}
          >
            <IconDownload size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>
      {standalone}
    </Box>
  );
}
